import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useCallback, useEffect, useState } from "react";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { Loader } from "@whoosh/design-system/src/components/display";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { AddOrEditSignUp } from "@whoosh/features/src/Events";
import { EventSignUpRow } from "@whoosh/features/src/Events/features/AddorEditSignUp/types";
import { EventPlayer } from "@whoosh/features/src/Events/types";

import { AddPatrons } from "../../../../features/AddPatrons";
import { Patron } from "../../../../features/AddPatrons/types";
import { currentProfileAtom } from "../../../../features/Club/atoms";
import { useFlags } from "../../../../utils/launchDarkly/useFlags";
import { eventSignUpsReducerAtom } from "../../atoms";
import { closeEventSignUpModalAtom } from "../../atoms/actions/closeEventSignUpModal/closeEventSignUpModalAtom";
import { eventSignUpModalStatusAtom } from "../../atoms/stores/eventSignUpModal";
import { useInitEventSignUpPrimaryChargeInfo } from "../../atoms/useInitEventSignUpPrimaryChargeInfo";
import { AddEventSignUpToConfirmedInput } from "../../hooks/__generated__/useAddEventSignUpToConfirmedMutation.graphql";
import { useAddEventSignUpToConfirmed } from "../../hooks/useAddEventSignUpToConfirmed";
import { useAddEventSignUpToWaitlist } from "../../hooks/useAddEventSignUpToWaitlistMutation";
import { useUpdateEventSignUp } from "../../hooks/useUpdateEventSignUp";
import { UpdateAttendeesGroupEventsModal_event$key } from "./__generated__/UpdateAttendeesGroupEventsModal_event.graphql";

type UpdateAttendeesGroupEventsModalProps = {
  eventId: string;
  fragmentRef: UpdateAttendeesGroupEventsModal_event$key;
  defaultPaymentCardOnFileId?: string;
  unknownGuestsAllowed: boolean;
};

export const UpdateAttendeesGroupEventsModal = ({
  defaultPaymentCardOnFileId,
  eventId,
  fragmentRef,
  unknownGuestsAllowed,
}: UpdateAttendeesGroupEventsModalProps) => {
  const { newPatronEventBookingFlow } = useFlags();
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const data = useFragment(
    UpdateAttendeesGroupEventsModalFragmentString,
    fragmentRef
  );
  useInitEventSignUpPrimaryChargeInfo({ eventRef: data });

  const currentProfile = useAtomValue(currentProfileAtom);
  const currentMemberUserId = currentProfile?.memberId;

  const { isOpen, overrideStep } = useAtomValue(eventSignUpModalStatusAtom);
  const [isFormDisplaying, setIsFormDisplaying] = useState(isOpen);
  const closeEventSignUpModal = useSetAtom(closeEventSignUpModalAtom);

  const [isAddingNewGuest, setIsAddingNewGuest] = useState(false);

  const [isDirtyFormStep, setIsDirtyFormStep] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  useEffect(() => {
    setIsFormDisplaying(isOpen);
  }, [isOpen]);

  const currentEventSignUp = useAtomValue(eventSignUpsReducerAtom);

  const isNewSignup = !currentEventSignUp.id;
  const supportsPublic = data.eventSeries?.facility?.club?.supportsPublic;
  const draftPaymentCardOnFileId =
    supportsPublic && newPatronEventBookingFlow
      ? defaultPaymentCardOnFileId
      : undefined;

  const handleCreateSuccess = useCallback(
    (signUpId?: string | null) => {
      closeEventSignUpModal();

      if (newPatronEventBookingFlow && signUpId) {
        navigation.navigate("EventSignupSummary", {
          eventID: eventId,
          signUpId,
        });
      }
    },
    [closeEventSignUpModal, eventId, navigation, newPatronEventBookingFlow]
  );

  const [addRegistrationToConfirmed, isAddingRegistrationToConfirmed] =
    useAddEventSignUpToConfirmed(handleCreateSuccess);

  const [addRegistrationToWaitlist, isAddingRegistrationToWaitlist] =
    useAddEventSignUpToWaitlist(handleCreateSuccess);

  const successCallback = useCallback(() => {
    closeEventSignUpModal();
  }, [closeEventSignUpModal]);

  const [updateEventSignUp, isUpdatingEventSignup] =
    useUpdateEventSignUp(successCallback);

  const handleSubmit = useCallback(
    (eventSignUpRow: EventSignUpRow) => {
      setIsFormDisplaying(false);
      const eventSignUp = {
        memberRegistrations: eventSignUpRow.registrations.flatMap(
          (registration, i) => {
            return registration.type === "MEMBER"
              ? [
                  {
                    id: registration.id,
                    index: registration.index ?? i,
                    payingMemberId: registration.payingMemberId,
                    playerId: registration.registrant.playerId ?? null,
                    formResponses: registration.formResponses,
                    primaryRegistrationChargeTypeId:
                      registration.primaryRegistrationChargeTypeId,
                  },
                ]
              : [];
          }
        ),
        guestRegistrations: eventSignUpRow.registrations.flatMap(
          (registration, i) => {
            return registration.type === "GUEST"
              ? [
                  {
                    id: registration.id,
                    index: registration.index ?? i,
                    payingMemberId: registration.payingMemberId,
                    playerId: registration.registrant.playerId ?? null,
                    formResponses: registration.formResponses,
                    primaryRegistrationChargeTypeId:
                      registration.primaryRegistrationChargeTypeId,
                  },
                ]
              : [];
          }
        ),
        newGuestRegistrations: eventSignUpRow.registrations.flatMap(
          (registration, i) => {
            return registration.type === "New Guest"
              ? [
                  {
                    id: registration.id ? registration.id : undefined,
                    index: registration.index ?? i,
                    payingMemberId: registration.payingMemberId,
                    name:
                      registration.registrant.firstName +
                      " " +
                      registration.registrant.lastName,
                    formResponses: registration.formResponses,
                    email: registration.registrant.email,
                    primaryRegistrationChargeTypeId:
                      registration.primaryRegistrationChargeTypeId,
                  },
                ]
              : [];
          }
        ),
        customerRegistrations: eventSignUpRow.registrations.flatMap(
          (registration, i) => {
            return registration.type === "CUSTOMER"
              ? [
                  {
                    id: registration.id ? registration.id : undefined,
                    index: registration.index ?? i,
                    payingCustomerId: registration.payingMemberId,
                    playerId: registration.registrant.playerId ?? null,
                    formResponses: registration.formResponses,
                    primaryRegistrationChargeTypeId:
                      registration.primaryRegistrationChargeTypeId,
                  },
                ]
              : [];
          }
        ),
      };

      const input: AddEventSignUpToConfirmedInput = {
        eventId,
        notifyMember: true,
        eventSignUp: {
          ...eventSignUp,
          isDraft: newPatronEventBookingFlow ? true : undefined,
          paymentCardOnFileId: draftPaymentCardOnFileId,
        },
      };

      const shouldBeOnWaitlist =
        data.capacityType === "ATTENDEES" &&
        data.spotsAvailable &&
        eventSignUpRow.registrations.length > data.spotsAvailable;

      if (isNewSignup) {
        data.accessStatusForClient === "WAITLIST_AVAILABLE" ||
        shouldBeOnWaitlist
          ? addRegistrationToWaitlist(input)
          : addRegistrationToConfirmed(input);
      } else if (eventSignUpRow.id) {
        const updateInput = {
          notifyMember: true,
          eventSignUp,
          eventSignUpId: eventSignUpRow.id,
        };
        updateEventSignUp(updateInput);
      }
    },
    [
      addRegistrationToConfirmed,
      addRegistrationToWaitlist,
      data.accessStatusForClient,
      data.capacityType,
      data.spotsAvailable,
      draftPaymentCardOnFileId,
      eventId,
      isNewSignup,
      newPatronEventBookingFlow,
      updateEventSignUp,
    ]
  );

  const isLoading =
    isUpdatingEventSignup ||
    isAddingRegistrationToConfirmed ||
    isAddingRegistrationToWaitlist;

  const currentPatron: Patron = {
    playerId: currentProfile?.memberId || "",
    firstName: currentProfile?.firstName || "",
    lastName: currentProfile?.lastName || "",
    profileImageId: currentProfile?.profileImagePublicId || "",
    transportationType: undefined, // TODO:1 We need to get this from the current user but I'm not sure what slotId to use
    type: supportsPublic ? "Customer" : "Member", // TODO: Why do I need to cast this but not above ??????
    memberGroups:
      currentProfile?.memberGroups?.flatMap((g) => (g?.id ? [g.id] : [])) ?? [],
  };

  const memberHoldEnabled = !!data?.memberHoldsAllowed;

  return (
    <>
      <StandardModal
        hideCloseButton
        disableContentScroll
        lockFullHeight
        isOpen={isOpen}
        onClose={() => {
          if (isDirtyFormStep) {
            setConfirmationModalOpen(true);
          } else {
            closeEventSignUpModal();
          }
        }}
        content={
          isLoading || !isFormDisplaying ? (
            <Loader />
          ) : (
            <SafeAreaProvider>
              <AddOrEditSignUp
                currentMemberUserId={currentMemberUserId}
                isOpen={isFormDisplaying}
                signUpRow={currentEventSignUp}
                eventRef={data}
                setIsDirtyFormStep={setIsDirtyFormStep}
                onSaveCallback={handleSubmit}
                onCancelCallback={() => {
                  closeEventSignUpModal();
                }}
                saveIsDisabled={isAddingNewGuest}
                stepToStartOn={overrideStep ?? undefined}
                isWaitlistSignup={
                  isNewSignup &&
                  data.accessStatusForClient === "WAITLIST_AVAILABLE"
                }
                addPeopleRenderFunction={({
                  isGroupFullOfGuests,
                  isGroupFullOfMembers,
                  isGroupFullOfAttendees,
                  isGroupFullOfCustomers,
                  selectedPlayerIds,
                  searchString,
                  setSearchString,
                  unknownGuestCount,
                  memberHoldCount,
                  handleAddMemberClick,
                  handleAddGuestClick,
                  handleNewGuestSubmission,
                  handleAddUnknownGuestClick,
                  handleAddMemberHoldClick,
                  handleRemoveMemberHoldClick,
                  handleRemoveUnknownGuestClick,
                  handleAddCustomerClick,
                  handleAddUnknownCustomerClick,
                  handleRemoveUnknownCustomerClick,
                  handleRemovePlayerClick,
                  currentRegistrations,
                }) => {
                  const unknownCustomerCount = currentRegistrations.filter(
                    (registration) =>
                      registration.type === "CUSTOMER" &&
                      !registration.registrant.playerId
                  ).length;
                  const unknownPlayerCount = supportsPublic
                    ? unknownCustomerCount
                    : unknownGuestCount;
                  const selectedPatrons = currentRegistrations.flatMap(
                    (registration): Patron[] => {
                      const type =
                        registration.type === "MEMBER"
                          ? "Member"
                          : registration.type === "CUSTOMER"
                          ? "Customer"
                          : null;
                      const playerId = registration.registrant.playerId;

                      if (!type || !playerId) return [];

                      return [
                        {
                          playerId,
                          firstName: registration.registrant.firstName,
                          lastName: registration.registrant.lastName,
                          email: registration.registrant.email ?? undefined,
                          phoneNumber:
                            registration.registrant.phoneNumber ?? undefined,
                          profileImageId:
                            registration.registrant.profileImage?.publicId ??
                            null,
                          type,
                          memberGroups: [],
                        },
                      ];
                    }
                  );

                  return (
                    <AddPatrons
                      playerNounOverride={
                        data.eventSeries?.facility?.copyOverrides
                          ?.eventPlayer ?? undefined
                      }
                      supportsPublic={!!supportsPublic}
                      includeTransportationType={false}
                      eventIdToExcludeParticipantsFromResults={data.id}
                      currentPatron={currentPatron}
                      isGroupFullOfGuests={isGroupFullOfGuests}
                      isGroupFullOfMembers={isGroupFullOfMembers}
                      isGroupFullOfAttendees={isGroupFullOfAttendees}
                      isGroupFullOfCustomers={isGroupFullOfCustomers}
                      checkNewDisabled={
                        isGroupFullOfMembers && isGroupFullOfGuests
                      }
                      players={selectedPlayerIds}
                      allPlayers={selectedPlayerIds}
                      selectedPatrons={selectedPatrons}
                      search={searchString}
                      memberHoldEnabled={memberHoldEnabled}
                      setSearch={setSearchString}
                      unknownGuestsEnabled={unknownGuestsAllowed}
                      handleAddMemberClick={handleAddMemberClick}
                      handleAddGuestClick={handleAddGuestClick}
                      handleAddCustomerClick={handleAddCustomerClick}
                      handleAddUnknownCustomerClick={
                        handleAddUnknownCustomerClick
                      }
                      onRemoveUnknownCustomer={handleRemoveUnknownCustomerClick}
                      onAddUnknownGuest={handleAddUnknownGuestClick}
                      onRemoveUnknownGuest={handleRemoveUnknownGuestClick}
                      onAddMemberHold={handleAddMemberHoldClick}
                      onRemoveMemberHold={handleRemoveMemberHoldClick}
                      bg="surface-primary"
                      onRemovePlayer={handleRemovePlayerClick}
                      keepItemInListAfterCheck
                      areGuestsAllowed
                      canAddNewGuest
                      setIsAddingNewGuest={setIsAddingNewGuest}
                      isAddingNewGuest={isAddingNewGuest}
                      unknownGuestCount={unknownPlayerCount}
                      memberHoldCount={memberHoldCount}
                      unknownCustomerEnabled={!!data.unknownGuestsAllowed}
                      onNewGuestSubmission={(newGuestFormProps, onFinish) => {
                        const player: EventPlayer = {
                          firstName: newGuestFormProps.fullName.split(" ")[0],
                          lastName: newGuestFormProps.fullName.split(" ")[1],
                          email: newGuestFormProps.email,
                        };
                        handleNewGuestSubmission(player);
                        onFinish();
                      }}
                    />
                  );
                }}
              />
            </SafeAreaProvider>
          )
        }
      />
      <PopUpModal
        isOpen={confirmationModalOpen}
        upperTitle={t`Close Event Form?`}
        cancelText={t`Cancel`}
        confirmText={t`Close`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            setConfirmationModalOpen(false);
            closeEventSignUpModal();
          } else {
            setConfirmationModalOpen(false);
          }
        }}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you want to close the event form modal?`}
            </Text>
          </VStack>
        }
      />
    </>
  );
};

const UpdateAttendeesGroupEventsModalFragmentString = graphql`
  fragment UpdateAttendeesGroupEventsModal_event on Event {
    id
    signUpStatusForClient(clientId: $memberId)
    accessStatusForClient(clientId: $memberId)
    unknownGuestsAllowed
    memberHoldsAllowed
    capacityType
    spotsAvailable
    eventSeries {
      facility {
        club {
          supportsPublic
        }
        copyOverrides {
          eventPlayer {
            singular
            plural
          }
        }
      }
    }
    signUpWindows {
      edges {
        node {
          cancelDeadlineAt
          cancelDeadlineDaysAhead
          cancelDeadlineTime
        }
      }
    }
    primaryRegistrationChargeTypes {
      edges {
        node {
          id
        }
      }
    }
    ...EventDetailGroupDisplay_event
    ...useInitEventSignUpPrimaryChargeInfo_event
    ...AddOrEditSignUp_event
  }
`;
